import {
  CloudArrowUpIcon,
  CircleStackIcon,
  ChatBubbleBottomCenterTextIcon,
  DocumentTextIcon,
  FolderIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

/**
 * Agent Mesh source registry — single place for workspace-visible retrieval sources.
 * Aligns with workspace Text-to-SQL plan: source-oriented tools (BigQuery, HubSpot, …).
 */

export type AgentMeshSourceCategory =
  | 'crm'
  | 'meetings'
  | 'notes'
  | 'collaboration'
  | 'documents'
  | 'catalog'
  | 'business_systems'
  | 'structured_data';

export interface AgentMeshSourceDefinition {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  category: AgentMeshSourceCategory;
  /** Section heading in the workspace panel */
  categoryLabel: string;
  /** Sort order within category */
  order: number;
  /**
   * When true and there is no tenant connection yet, hide "Add in connectors" and
   * show a muted note (warehouse onboarding not in Data Connections UI yet).
   */
  connectionUiComingSoon: boolean;
  placeholder?: string;
  description?: string;
}

const CATEGORY_ORDER: Record<AgentMeshSourceCategory, number> = {
  crm: 0,
  structured_data: 1,
  business_systems: 2,
  catalog: 3,
  meetings: 4,
  notes: 5,
  collaboration: 6,
  documents: 7,
};

export const AGENT_MESH_SOURCE_REGISTRY = {
  hubspot: {
    id: 'hubspot',
    label: 'HubSpot',
    icon: IdentificationIcon,
    category: 'crm',
    categoryLabel: 'CRM & customer data',
    order: 10,
    connectionUiComingSoon: false,
    placeholder: 'pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    description: 'Paste a HubSpot private-app access token or developer API key. It will be validated against the HubSpot API before saving.',
  },
  bigquery: {
    id: 'bigquery',
    label: 'BigQuery',
    icon: CircleStackIcon,
    category: 'structured_data',
    categoryLabel: 'Structured data (warehouses)',
    order: 10,
    connectionUiComingSoon: false,
  },
  postgres: {
    id: 'postgres',
    label: 'PostgreSQL',
    icon: CircleStackIcon,
    category: 'structured_data',
    categoryLabel: 'Structured data (warehouses)',
    order: 20,
    connectionUiComingSoon: false,
  },
  mariadb: {
    id: 'mariadb',
    label: 'MariaDB',
    icon: CircleStackIcon,
    category: 'structured_data',
    categoryLabel: 'Structured data (warehouses)',
    order: 30,
    connectionUiComingSoon: false,
    description: 'Query selected MariaDB tables live through the governed Agent Mesh runtime.',
  },
  erp: {
    id: 'erp',
    label: 'ERP',
    icon: CircleStackIcon,
    category: 'business_systems',
    categoryLabel: 'Business systems',
    order: 10,
    connectionUiComingSoon: false,
    placeholder: 'https://erp.example.com',
    description: 'Query selected ERP tables synced into erp_analytics through the structured Agent Mesh runtime.',
  },
  fathom: {
    id: 'fathom',
    label: 'Fathom',
    icon: ChatBubbleBottomCenterTextIcon,
    category: 'meetings',
    categoryLabel: 'Meetings & transcripts',
    order: 10,
    connectionUiComingSoon: false,
    placeholder: 'fathom_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx',
    description: 'Paste your Fathom API key (from User Settings → API Access). It will be validated against the Fathom API before saving.',
  },
  granola: {
    id: 'granola',
    label: 'Granola',
    icon: DocumentTextIcon,
    category: 'notes',
    categoryLabel: 'Notes & knowledge capture',
    order: 10,
    connectionUiComingSoon: false,
  },
  slack: {
    id: 'slack',
    label: 'Slack',
    icon: ChatBubbleBottomCenterTextIcon,
    category: 'collaboration',
    categoryLabel: 'Collaboration',
    order: 10,
    connectionUiComingSoon: false,
    description: 'Search selected Slack channels and workspace messages.',
  },
  google_drive: {
    id: 'google_drive',
    label: 'Google Drive',
    icon: FolderIcon,
    category: 'documents',
    categoryLabel: 'Documents & knowledge',
    order: 5,
    connectionUiComingSoon: false,
    description: 'Search files from a configured Google Drive folder or shared drive.',
  },
  knowledge_base: {
    id: 'knowledge_base',
    label: 'Knowledge Base',
    icon: DocumentTextIcon,
    category: 'documents',
    categoryLabel: 'Documents & knowledge',
    order: 10,
    connectionUiComingSoon: false,
    description: 'Semantic search across uploaded company documents, policies, and reports.',
  },
  filesystem: {
    id: 'filesystem',
    label: 'Local Files',
    icon: FolderIcon,
    category: 'documents',
    categoryLabel: 'Documents & file storage',
    order: 10,
    connectionUiComingSoon: false,
  },
  s3: {
    id: 's3',
    label: 'Amazon S3',
    icon: CloudArrowUpIcon,
    category: 'documents',
    categoryLabel: 'Documents & file storage',
    order: 20,
    connectionUiComingSoon: false,
  },
  atlan: {
    id: 'atlan',
    label: 'Atlan',
    icon: CircleStackIcon,
    category: 'catalog',
    categoryLabel: 'Data Catalog (Enrichment)',
    order: 10,
    connectionUiComingSoon: false,
    placeholder: 'https://tenant.atlan.com',
    description: 'Provide your Atlan host and API key. This will be used to enrich warehouse metadata with business context.',
  },
} as const satisfies Record<string, AgentMeshSourceDefinition>;

export type AgentMeshSourceType = keyof typeof AGENT_MESH_SOURCE_REGISTRY;

export const AGENT_MESH_SOURCE_TYPES = Object.keys(
  AGENT_MESH_SOURCE_REGISTRY,
) as readonly AgentMeshSourceType[];

export function isAgentMeshSourceType(value: string): value is AgentMeshSourceType {
  return (AGENT_MESH_SOURCE_TYPES as readonly string[]).includes(value);
}

export function getAgentMeshSourceDefinition(sourceType: AgentMeshSourceType): AgentMeshSourceDefinition {
  return AGENT_MESH_SOURCE_REGISTRY[sourceType];
}

export function groupAgentMeshSourcesByCategory(): {
  category: AgentMeshSourceCategory;
  label: string;
  sources: AgentMeshSourceDefinition[];
}[] {
  const buckets = new Map<AgentMeshSourceCategory, AgentMeshSourceDefinition[]>();
  for (const sourceType of AGENT_MESH_SOURCE_TYPES) {
    const def = AGENT_MESH_SOURCE_REGISTRY[sourceType];
    const list = buckets.get(def.category) ?? [];
    list.push(def as unknown as AgentMeshSourceDefinition);
    buckets.set(def.category, list);
  }
  Array.from(buckets.values()).forEach((list) => {
    list.sort(
      (a: AgentMeshSourceDefinition, b: AgentMeshSourceDefinition) =>
        a.order - b.order || a.id.localeCompare(b.id),
    );
  });
  const categories = Array.from(buckets.keys()).sort(
    (a: AgentMeshSourceCategory, b: AgentMeshSourceCategory) =>
      CATEGORY_ORDER[a] - CATEGORY_ORDER[b],
  );
  return categories.map((category) => ({
    category,
    label: buckets.get(category)![0]!.categoryLabel,
    sources: buckets.get(category)!,
  }));
}
